import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Video, ChevronRight, CheckCircle, Clock, XCircle, ExternalLink, Image as ImageIcon } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { formatDate } from '../../lib/utils';
import { fromSlug, invitationUrlSegment } from '../../lib/slug';

export default function KOLSubmissionDetail() {
  const { id: rawId } = useParams();
  const id = fromSlug(rawId || '');
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubmission();
  }, [id]);

  const loadSubmission = async () => {
    try {
      const response = await api.get(`/submissions/${id}`);
      setSubmission(response.data);
    } catch (error) {
      console.error('Failed to load submission:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return <Badge variant="default">Submitted</Badge>;
      case 'APPROVED': return <Badge variant="success">Approved</Badge>;
      case 'REVISION_REQUESTED': return <Badge variant="destructive">Revision Requested</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Loading...</div>;
  }

  if (!submission) {
    return <div className="text-center py-12 text-gray-500">Submission not found</div>;
  }

  const job = submission.invitation?.job;
  // const kolProfile = submission.invitation?.kolProfile;

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-1 text-sm text-gray-500">
        <button onClick={() => navigate('/kol/submissions')} className="hover:text-green-600 transition-colors">Submissions</button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium truncate max-w-md">{job?.title || 'Submission'}</span>
      </nav>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/kol/submissions')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="page-title">Submission</h1>
              {getStatusBadge(submission.status)}
            </div>
            <p className="text-gray-600 mt-1">Version {submission.version} • {job?.title || 'Unknown Campaign'}</p>
          </div>
        </div>
      </div>

      {/* Status Banners */}
      {submission.status === 'APPROVED' && (
        <Card className="border-l-4 border-l-green-500 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-green-900 font-medium">Your content has been approved!</p>
            </div>
          </CardContent>
        </Card>
      )}

      {submission.status === 'REVISION_REQUESTED' && (
        <Card className="border-l-4 border-l-red-500">
          <CardHeader>
            <CardTitle className="text-red-900 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Revision Requested
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-800">{submission.revisionComments || 'Please revise and resubmit your content.'}</p>
            <p className="text-sm text-gray-500 mt-3">
              Go to your{' '}
              <button
                type="button"
                onClick={() => {
                  if (!submission.invitation?.id) return;
                  navigate(
                    `/kol/invitations/${encodeURIComponent(
                      invitationUrlSegment({
                        id: submission.invitation.id,
                        urlSlug: submission.invitation.urlSlug,
                        job: submission.invitation.job
                          ? {
                              title: submission.invitation.job.title,
                              urlSlug: submission.invitation.job.urlSlug,
                            }
                          : undefined,
                      })
                    )}`
                  );
                }}
                className="text-green-600 hover:underline"
              >
                invitation
              </button>{' '}
              to submit a revised version.
            </p>
          </CardContent>
        </Card>
      )}

      {submission.status === 'SUBMITTED' && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <p className="text-blue-900 font-medium">Your submission is under review by the brand.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Caption */}
          {submission.captionText && (
            <Card>
              <CardHeader>
                <CardTitle>Caption</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="whitespace-pre-wrap text-sm text-gray-800">{submission.captionText}</pre>
              </CardContent>
            </Card>
          )}

          {/* Media */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                Media ({submission.media?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!submission.media || submission.media.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No media attached</p>
              ) : (
                <div className="space-y-4">
                  {submission.media.map((m: any) => (
                    <div key={m.id} className="border rounded-lg p-4">
                      {m.type === 'IMAGE' ? (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <ImageIcon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">{m.title || 'Image'}</span>
                          </div>
                          <img src={m.url} alt={m.title || 'Submission media'} className="max-w-full rounded-lg max-h-96 object-contain" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Video className="w-4 h-4 text-purple-500" />
                            <span className="text-sm font-medium text-gray-700">{m.title || 'Video'}</span>
                          </div>
                          <a href={m.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                            Open <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Campaign</p>
                <p className="font-medium text-gray-900 mt-1">{job?.title || 'Unknown'}</p>
                {job?.brandOrg?.name && (
                  <p className="text-sm text-gray-500">{job.brandOrg.name}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Version</p>
                <p className="font-medium text-gray-900 mt-1">v{submission.version}</p>
              </div>
              {submission.createdBy && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Created By</p>
                  <p className="font-medium text-gray-900 mt-1">{submission.createdBy.fullName || submission.createdBy.email}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Submitted</p>
                <p className="text-sm text-gray-700 mt-1">{formatDate(submission.createdAt)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
